<template>
  <div class="rate-limit-card">
    <div class="card-header">
      <h4>Rate Limit Monitor</h4>
      <div class="refresh-indicator" v-if="loading">
        <div class="spinner-small"></div>
        <span>Refreshing...</span>
      </div>
    </div>

    <!-- Current Stats -->
    <div class="current-stats">
      <div class="stat-item">
        <span class="stat-label">Current Requests</span>
        <span class="stat-value" :class="{ 'warning': utilizationPercent > 70, 'danger': utilizationPercent > 90 }">
          {{ rateLimitData.current_requests }} / {{ rateLimitData.requests_per_minute }}
        </span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Utilization</span>
        <div class="progress-bar-container">
          <div 
            class="progress-bar" 
            :class="{ 'warning': utilizationPercent > 70, 'danger': utilizationPercent > 90 }"
            :style="{ width: utilizationPercent + '%' }"
          ></div>
          <span class="progress-text">{{ utilizationPercent }}%</span>
        </div>
      </div>
      <div class="stat-item">
        <span class="stat-label">Window</span>
        <span class="stat-value">{{ rateLimitData.window_seconds }}s</span>
      </div>
    </div>

    <!-- Historical Chart -->
    <div class="chart-section">
      <h5>Request History (Last {{ rateLimitData.window_seconds }}s)</h5>
      <div v-if="!rateLimitData.history || rateLimitData.history.length === 0" class="empty-state">
        <p>No historical data available</p>
      </div>
      <div v-else class="chart-container">
        <div class="y-axis">
          <div class="y-label">{{ maxRequests }}</div>
          <div class="y-label">{{ Math.round(maxRequests * 0.75) }}</div>
          <div class="y-label">{{ Math.round(maxRequests * 0.5) }}</div>
          <div class="y-label">{{ Math.round(maxRequests * 0.25) }}</div>
          <div class="y-label">0</div>
        </div>
        <div class="chart-area">
          <div class="chart-grid">
            <div class="grid-line" v-for="i in 5" :key="i"></div>
          </div>
          <div class="chart-bars">
            <div 
              v-for="(point, index) in displayHistory" 
              :key="index"
              class="bar-column"
            >
              <div class="bar-wrapper">
                <div 
                  class="bar"
                  :style="{ height: getBarHeight(point.count) + '%' }"
                  :title="`${formatTime(point.timestamp)}: ${point.count} requests`"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RateLimitChart',
  props: {
    rateLimitData: {
      type: Object,
      required: true,
      default: () => ({
        requests_per_minute: 100,
        window_seconds: 60,
        current_requests: 0,
        history: []
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    utilizationPercent() {
      if (!this.rateLimitData.requests_per_minute) return 0;
      return Math.round((this.rateLimitData.current_requests / this.rateLimitData.requests_per_minute) * 100);
    },
    displayHistory() {
      // Show last 60 data points for better visualization
      const history = this.rateLimitData.history || [];
      return history.slice(-60);
    },
    maxRequests() {
      if (!this.displayHistory || this.displayHistory.length === 0) return 10;
      const max = Math.max(...this.displayHistory.map(h => h.count));
      return Math.max(max, 1);
    }
  },
  methods: {
    getBarHeight(count) {
      if (!this.maxRequests || this.maxRequests === 0) return 0;
      return (count / this.maxRequests) * 100;
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
  }
};
</script>

<style scoped>
.rate-limit-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.current-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  font-weight: 600;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
}

.stat-value.warning {
  color: #f59e0b;
}

.stat-value.danger {
  color: #ef4444;
}

.progress-bar-container {
  position: relative;
  height: 32px;
  background: var(--form-bg);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--border-default);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  transition: width 0.3s ease, background 0.3s ease;
  border-radius: 6px;
}

.progress-bar.warning {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.progress-bar.danger {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.chart-section {
  margin-top: 24px;
}

.chart-section h5 {
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #9ca3af;
}

.chart-container {
  display: flex;
  gap: 8px;
  height: 200px;
}

.y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 40px;
  height: 150px;
  font-size: 11px;
  color: #9ca3af;
  text-align: right;
  padding-right: 8px;
}

.y-label {
  line-height: 1;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.chart-area {
  flex: 1;
  position: relative;
  height: 150px;
}

.chart-grid {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.grid-line {
  border-bottom: 1px solid var(--border-default);
  width: 100%;
}

.chart-bars {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 150px;
  display: flex;
  gap: 2px;
  padding: 0 4px;
}

.bar-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 2px;
  height: 100%;
}

.bar-wrapper {
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.bar {
  width: 100%;
  background: linear-gradient(180deg, var(--primary-start), #5850e6);
  border-radius: 2px 2px 0 0;
  min-height: 2px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.bar:hover {
  opacity: 0.8;
  transform: translateY(-2px);
}
</style>
